import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, ArrowRight, Upload, X, Star, Check, Image as ImageIcon, Tag, DollarSign, FileText } from 'lucide-react';
import { categories } from '../mock/mockData';
import { productsAPI } from '../services/api';
import { getImageUrl, formatPrice } from '../utils/helpers';
import { toast } from '../hooks/use-toast';
import { VariantsEditor, isVariantValid, serializeVariants, hydrateVariants } from './VariantsEditor';
import { PricingCard } from './PricingCard';

const STEPS = [
  { label: 'Información', icon: Tag },
  { label: 'Precio y Stock', icon: DollarSign },
  { label: 'Imágenes', icon: ImageIcon },
  { label: 'Publicar', icon: Check },
];

const CONDITIONS = ['Nuevo', 'Usado', 'Reacondicionado'];

const ProductWizard = ({ onClose, onSuccess, editingProduct, user }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [imageFiles, setImageFiles] = useState([null, null, null, null, null]);
  const [imagePreviews, setImagePreviews] = useState(() => {
    if (editingProduct?.images) {
      const p = ['', '', '', '', ''];
      editingProduct.images.forEach((img, i) => { if (i < 5) p[i] = img; });
      return p;
    }
    return ['', '', '', '', ''];
  });
  const [primaryImageIndex, setPrimaryImageIndex] = useState(editingProduct?.primary_image_index || 0);
  const [formData, setFormData] = useState({
    title: editingProduct?.title || '',
    category: editingProduct?.category || '',
    condition: editingProduct?.condition || 'Nuevo',
    price: editingProduct?.price?.toString() || '',
    compare_at_price: (editingProduct?.compare_at_price ?? editingProduct?.original_price)?.toString() || '',
    cost_price: editingProduct?.cost_price?.toString() || '',
    stock: editingProduct?.stock?.toString() || '',
    description: editingProduct?.description || '',
    features: editingProduct?.features?.join('\n') || '',
    shipping_cost: editingProduct?.shipping_info?.shipping_cost?.toString() || '',
    carrier_name: editingProduct?.shipping_info?.carrier_name || 'Envío estándar',
    delivery_days: editingProduct?.shipping_info?.delivery_days?.toString() || '5',
  });

  const [variants, setVariants] = useState(() => hydrateVariants(editingProduct?.variants));
  const hasVariants = variants.length > 0;

  const set = (key, val) => setFormData(p => ({ ...p, [key]: val }));

  // ===== IMAGE LOGIC (preserved exactly) =====
  const handleImageChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFiles(prev => { const n = [...prev]; n[index] = file; return n; });
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviews(prev => { const n = [...prev]; n[index] = reader.result; return n; });
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (index) => {
    setImageFiles(prev => { const n = [...prev]; n[index] = null; return n; });
    setImagePreviews(prev => { const n = [...prev]; n[index] = ''; return n; });
    if (primaryImageIndex === index) setPrimaryImageIndex(0);
  };

  const extractRelativePath = (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return null;
    if (url.includes('/uploads/')) return url.substring(url.indexOf('/uploads/'));
    if (url.startsWith('/')) return url;
    return url;
  };

  // ===== SUBMIT (preserved exactly) =====
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const finalImages = [];
      for (let i = 0; i < 5; i++) {
        const file = imageFiles[i];
        const preview = imagePreviews[i];
        if (file) {
          try {
            const result = await productsAPI.uploadImage(file);
            if (result?.url) finalImages.push({ index: i, url: result.url });
          } catch (err) { console.error(`Error uploading image ${i}:`, err); }
        } else if (preview && preview.trim() && !preview.startsWith('data:')) {
          const rel = extractRelativePath(preview);
          if (rel) finalImages.push({ index: i, url: rel });
        }
      }

      if (finalImages.length === 0 && !editingProduct) {
        toast({ title: 'Error', description: 'Debes subir al menos una imagen', variant: 'destructive' });
        setLoading(false);
        return;
      }

      const orderedImages = [];
      const primary = finalImages.find(img => img.index === primaryImageIndex);
      if (primary) orderedImages.push(primary.url);
      finalImages.filter(img => img.index !== primaryImageIndex).sort((a, b) => a.index - b.index).forEach(img => orderedImages.push(img.url));

      const serialized = serializeVariants(variants);
      const productData = {
        title: formData.title,
        price: hasVariants ? Math.min(...serialized.map(v => v.price)) : parseFloat(formData.price),
        compare_at_price: hasVariants ? null : (parseFloat(formData.compare_at_price) || null),
        cost_price: hasVariants ? null : (formData.cost_price === '' ? null : parseFloat(formData.cost_price)),
        description: formData.description,
        category: formData.category,
        stock: hasVariants ? serialized.reduce((t, v) => t + v.stock, 0) : parseInt(formData.stock),
        variants: serialized,
        images: orderedImages.length > 0 ? orderedImages : editingProduct?.images,
        primary_image_index: 0,
        features: formData.features.split('\n').filter(f => f.trim()),
        condition: formData.condition,
        free_shipping: parseFloat(formData.shipping_cost || 0) === 0,
        shipping_info: {
          shipping_cost: parseFloat(formData.shipping_cost || 0),
          carrier_name: formData.carrier_name,
          delivery_days: parseInt(formData.delivery_days),
        },
      };

      if (editingProduct) {
        await productsAPI.update(editingProduct._id, productData);
        toast({ title: '¡Producto actualizado!', description: 'Los cambios han sido guardados' });
      } else {
        await productsAPI.create(productData);
        toast({ title: '¡Producto publicado!', description: 'Tu producto ha sido agregado al catálogo' });
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
      toast({ title: 'Error', description: editingProduct ? 'No se pudo actualizar' : 'No se pudo publicar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ===== VALIDATION =====
  const canAdvance = () => {
    if (step === 0) return formData.title.trim() && formData.category && formData.condition;
    if (step === 1) return (hasVariants ? variants.every(isVariantValid) : formData.price && formData.stock) && formData.description.trim();
    if (step === 2) return imagePreviews.some(p => p) || editingProduct;
    return true;
  };

  const hasImages = imagePreviews.filter(p => p).length;

  // ===== RENDER STEPS =====
  const renderStep0 = () => (
    <div className="space-y-5">
      <div>
        <Label className="text-base mb-2 block">Título del producto *</Label>
        <Input value={formData.title} onChange={e => set('title', e.target.value)}
          placeholder="Ej: iPhone 15 Pro Max 256GB" className="p-4 text-base" data-testid="wizard-title" />
      </div>
      <div>
        <Label className="text-base mb-2 block">Categoría *</Label>
        <Select value={formData.category} onValueChange={v => set('category', v)}>
          <SelectTrigger className="p-4 text-base" data-testid="wizard-category">
            <SelectValue placeholder="Selecciona una categoría" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-base mb-2 block">Condición *</Label>
        <div className="grid grid-cols-3 gap-3" data-testid="wizard-condition">
          {CONDITIONS.map(c => (
            <button key={c} type="button" onClick={() => set('condition', c)}
              className={`p-4 rounded-lg border-2 text-center font-medium transition-all ${
                formData.condition === c ? 'border-zl-orange bg-orange-50 text-zl-orange' : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}>
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5">
      {!hasVariants && (
        <PricingCard price={formData.price} compare={formData.compare_at_price} cost={formData.cost_price} stock={formData.stock} onChange={set} />
      )}
      <VariantsEditor variants={variants} onChange={setVariants} defaults={{ price: formData.price, compare_at_price: formData.compare_at_price, cost_price: formData.cost_price }} />
      <div>
        <Label className="text-base mb-2 block">Descripción *</Label>
        <Textarea value={formData.description} onChange={e => set('description', e.target.value)}
          placeholder="Describe tu producto en detalle..." rows={5} className="p-4 text-base" data-testid="wizard-description" />
      </div>
      <div className="border-t pt-4">
        <p className="text-sm text-gray-500 font-medium mb-3">Envío (opcional)</p>
        <div className="grid grid-cols-3 gap-3">
          <Input type="number" value={formData.shipping_cost} onChange={e => set('shipping_cost', e.target.value)}
            placeholder="Costo (0=gratis)" className="p-3 text-sm" />
          <Input value={formData.carrier_name} onChange={e => set('carrier_name', e.target.value)}
            placeholder="Transportista" className="p-3 text-sm" />
          <Input type="number" value={formData.delivery_days} onChange={e => set('delivery_days', e.target.value)}
            placeholder="Días" className="p-3 text-sm" />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <div>
        <Label className="text-base mb-2 block">Características (una por línea)</Label>
        <Textarea value={formData.features} onChange={e => set('features', e.target.value)}
          placeholder={"256GB almacenamiento\nTitanio Natural\nCámara 48MP"} rows={4} className="p-4 text-base" data-testid="wizard-features" />
      </div>
      <div>
        <Label className="text-base mb-2 block">Imágenes del producto (hasta 5) *</Label>
        <p className="text-sm text-gray-500 mb-3">Toca la estrella para marcar la imagen principal</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {[0, 1, 2, 3, 4].map(idx => (
            <div key={idx} className={`relative border-2 border-dashed rounded-xl overflow-hidden aspect-square transition-colors ${
              primaryImageIndex === idx && imagePreviews[idx] ? 'border-zl-orange bg-orange-50' : 'border-gray-300 hover:border-gray-400'
            }`}>
              {imagePreviews[idx] ? (
                <>
                  <img
                    src={imagePreviews[idx].startsWith('data:') || imagePreviews[idx].startsWith('http') ? imagePreviews[idx] : getImageUrl(imagePreviews[idx])}
                    alt={`Img ${idx + 1}`} className="w-full h-full object-cover"
                    onError={e => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }} />
                  <button type="button" onClick={() => setPrimaryImageIndex(idx)}
                    className={`absolute top-1 left-1 rounded-full p-1.5 ${primaryImageIndex === idx ? 'bg-zl-orange text-white' : 'bg-white/80 text-gray-400'}`}>
                    <Star className="w-4 h-4" fill={primaryImageIndex === idx ? 'currentColor' : 'none'} />
                  </button>
                  <button type="button" onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5">
                    <X className="w-3 h-3" />
                  </button>
                  {primaryImageIndex === idx && (
                    <div className="absolute bottom-0 inset-x-0 bg-zl-orange text-white text-xs py-0.5 text-center">Principal</div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full cursor-pointer p-2"
                  onClick={() => document.getElementById(`wiz-img-${idx}`).click()}>
                  <Upload className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">{idx + 1}</span>
                </div>
              )}
              <input id={`wiz-img-${idx}`} type="file" accept="image/*" onChange={e => handleImageChange(idx, e)} className="hidden" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const featuresList = formData.features.split('\n').filter(f => f.trim());
    const imgs = imagePreviews.filter(p => p);
    return (
      <div className="space-y-5" data-testid="wizard-summary">
        {/* Preview image */}
        {imgs.length > 0 && (
          <div className="rounded-xl overflow-hidden bg-gray-100">
            <img
              src={imgs[0].startsWith('data:') || imgs[0].startsWith('http') ? imgs[0] : getImageUrl(imgs[0])}
              alt="Preview" className="w-full h-48 sm:h-64 object-contain"
              onError={e => { e.target.src = 'https://via.placeholder.com/400x300'; }} />
          </div>
        )}
        <div>
          <p className="text-sm text-gray-500">{formData.condition} · {formData.category}</p>
          <h2 className="text-xl font-bold mt-1">{formData.title}</h2>
          {hasVariants ? (
            <div className="mt-2" data-testid="wizard-summary-variants">
              <p className="text-3xl font-bold text-zl-orange">Desde {formatPrice(Math.min(...variants.map(v => parseFloat(v.price) || 0)))}</p>
              <ul className="mt-2 space-y-1">
                {variants.map((v, i) => (
                  <li key={i} className="text-sm text-gray-700 flex justify-between border-b py-1">
                    <span>{[v.size, v.color].filter(Boolean).join(' / ')}</span>
                    <span>{formatPrice(parseFloat(v.price) || 0)} · {v.stock} uds</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="text-3xl font-bold text-zl-orange mt-2">{formatPrice(parseFloat(formData.price) || 0)}</p>
              <p className="text-sm text-gray-600 mt-1">Stock: {formData.stock} unidades</p>
            </>
          )}
        </div>
        <div className="border-t pt-3">
          <p className="font-semibold mb-1">Descripción</p>
          <p className="text-gray-700 text-sm">{formData.description}</p>
        </div>
        {featuresList.length > 0 && (
          <div className="border-t pt-3">
            <p className="font-semibold mb-1">Características</p>
            <ul className="space-y-1">
              {featuresList.map((f, i) => <li key={i} className="text-sm text-gray-700 flex"><span className="text-zl-orange mr-2">✓</span>{f}</li>)}
            </ul>
          </div>
        )}
        {imgs.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {imgs.slice(1).map((img, i) => (
              <img key={i}
                src={img.startsWith('data:') || img.startsWith('http') ? img : getImageUrl(img)}
                alt={`Extra ${i + 2}`} className="w-16 h-16 rounded object-cover flex-shrink-0"
                onError={e => { e.target.src = 'https://via.placeholder.com/64'; }} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="product-wizard">
      {/* Top bar */}
      <div className="sticky top-0 bg-white border-b z-10 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900 flex items-center gap-1">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Cancelar</span>
          </button>
          <h1 className="font-bold text-base sm:text-lg">
            {editingProduct ? 'Editar producto' : 'Nuevo producto'}
          </h1>
          <span className="text-sm text-gray-500">Paso {step + 1} de {STEPS.length}</span>
        </div>
        {/* Progress bar */}
        <div className="flex gap-1.5">
          {STEPS.map((s, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-zl-orange' : 'bg-gray-200'}`} />
          ))}
        </div>
        {/* Step labels */}
        <div className="flex justify-between mt-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <button key={i} onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-1 text-xs transition-colors ${
                  i === step ? 'text-zl-orange font-semibold' : i < step ? 'text-gray-500 cursor-pointer' : 'text-gray-300'
                }`}>
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto px-4 py-5 pb-28">
        {stepRenderers[step]()}
      </div>

      {/* Bottom navigation - fixed */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t p-4 flex gap-3 z-10" data-testid="wizard-nav">
        {step > 0 && (
          <Button variant="outline" className="flex-1 h-12 text-base" onClick={() => setStep(s => s - 1)} data-testid="wizard-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Atrás
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button className="flex-1 h-12 text-base bg-zl-orange hover:bg-zl-orange-600" disabled={!canAdvance()}
            onClick={() => setStep(s => s + 1)} data-testid="wizard-next">
            Siguiente <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button className="flex-1 h-12 text-base bg-green-600 hover:bg-green-700" disabled={loading}
            onClick={handleSubmit} data-testid="wizard-publish">
            {loading ? 'Publicando...' : (editingProduct ? 'Actualizar producto' : 'Publicar producto')}
            {!loading && <Check className="w-5 h-5 ml-2" />}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductWizard;
